/*
 * <hp-carousel-indicator aria-controls="<grid-carousel id>">
 * Slider'ın altında sayfa başına bir çubuk çizer (aktif: turuncu, diğerleri: gri).
 * Çubuğa tıklanınca ilgili sayfaya kaydırır. Hedef carousel id ile bulunur; carousel
 * DOM'da değiştirilse bile (sekme geçişi, geç yüklenen içerik) olaylar üzerinden yeniden bağlanır.
 */
(() => {
  if (customElements.get('hp-carousel-indicator')) return;

  const TRACK_SELECTOR = '#track';

  class HpCarouselIndicator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.pageCount = 0;
      this.activePage = 0;
      this.carousel = null;
      this.track = null;

      this.handleCarouselEvent = this.handleCarouselEvent.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.scheduleUpdate = this.scheduleUpdate.bind(this);

      document.addEventListener('scroll-carousel:update', this.handleCarouselEvent);
      document.addEventListener('carousel:update', this.handleCarouselEvent);
      window.addEventListener('resize', this.scheduleUpdate);
      this.addEventListener('click', this.handleClick);

      this.resolveCarousel();
      this.scheduleUpdate();
      // Geç yüklenen içerik / sekme değişimi için birkaç kez tekrar dene
      [300, 1000, 2500].forEach((delay) => window.setTimeout(() => this.resolveCarousel() && this.scheduleUpdate(), delay));
    }

    disconnectedCallback() {
      document.removeEventListener('scroll-carousel:update', this.handleCarouselEvent);
      document.removeEventListener('carousel:update', this.handleCarouselEvent);
      window.removeEventListener('resize', this.scheduleUpdate);
      this.unbindTrack();
    }

    get targetId() {
      return this.getAttribute('aria-controls') || '';
    }

    findCarousel() {
      // 1) controls slotunda: doğrudan içinde bulunduğu carousel
      const host = this.closest('grid-carousel');
      if (host) return host;
      // 2) aria-controls id'si
      const id = this.targetId;
      if (id) {
        const byId = document.getElementById(id);
        if (byId && byId.tagName.toLowerCase() === 'grid-carousel') return byId;
      }
      // 3) aynı section içindeki ilk carousel
      const scope = this.closest('.shopify-section') || this.parentElement;
      return scope ? scope.querySelector('grid-carousel') : null;
    }

    resolveCarousel() {
      const carousel = this.findCarousel();
      if (!carousel) return false;
      if (carousel !== this.carousel) {
        this.unbindTrack();
        this.carousel = carousel;
        this.bindTrack();
        window.hpEnableCarouselDragScroll?.(carousel, this.scheduleUpdate);
      } else if (!this.track) {
        this.bindTrack();
      }
      return true;
    }

    bindTrack() {
      const bind = () => {
        const track = this.carousel?.shadowRoot?.querySelector(TRACK_SELECTOR);
        if (!track) return false;
        this.track = track;
        track.addEventListener('scroll', this.handleScroll, { passive: true });
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.scheduleUpdate);
          this.resizeObserver.observe(track);
        }
        this.scheduleUpdate();
        return true;
      };

      if (bind()) return;
      const carousel = this.carousel;
      const retry = () => {
        if (this.carousel !== carousel) return;
        bind();
      };
      if (carousel?.updateComplete) {
        carousel.updateComplete.then(retry);
      } else {
        window.setTimeout(retry, 200);
      }
    }

    unbindTrack() {
      if (this.track) {
        this.track.removeEventListener('scroll', this.handleScroll);
      }
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      this.track = null;
    }

    handleCarouselEvent(event) {
      const target = event.target;
      if (!target || target.tagName?.toLowerCase() !== 'grid-carousel') return;
      if (target !== this.findCarousel()) return;
      if (target !== this.carousel) {
        this.unbindTrack();
        this.carousel = target;
        this.bindTrack();
        window.hpEnableCarouselDragScroll?.(target, this.scheduleUpdate);
      }
      this.scheduleUpdate();
    }

    handleScroll() {
      this.scheduleUpdate();
    }

    scheduleUpdate() {
      if (this.updateFrame) return;
      this.updateFrame = window.requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    }

    getMetrics() {
      const track = this.track;
      if (!track) return null;
      const viewport = track.clientWidth;
      const total = track.scrollWidth;
      if (!viewport || total <= viewport + 2) return { pages: 1, active: 0 };
      const maxScroll = total - viewport;
      const pages = Math.max(2, Math.ceil(total / viewport));
      const progress = Math.min(1, Math.max(0, track.scrollLeft / maxScroll));
      const active = Math.min(pages - 1, Math.round(progress * (pages - 1)));
      return { pages, active, viewport, maxScroll };
    }

    update() {
      const metrics = this.getMetrics();
      if (!metrics || metrics.pages <= 1) {
        this.hidden = true;
        this.pageCount = 0;
        return;
      }

      this.hidden = false;

      if (metrics.pages !== this.pageCount) {
        this.pageCount = metrics.pages;
        const fragment = document.createDocumentFragment();
        for (let index = 0; index < metrics.pages; index += 1) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'hp-carousel-indicator__segment';
          button.dataset.page = String(index);
          button.setAttribute('aria-label', `${index + 1} / ${metrics.pages}`);
          fragment.appendChild(button);
        }
        this.replaceChildren(fragment);
      }

      if (metrics.active !== this.activePage || this.querySelector('.is-active') === null) {
        this.activePage = metrics.active;
        this.querySelectorAll('.hp-carousel-indicator__segment').forEach((segment, index) => {
          const isActive = index === metrics.active;
          segment.classList.toggle('is-active', isActive);
          segment.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }
    }

    handleClick(event) {
      const segment = event.target.closest('.hp-carousel-indicator__segment');
      if (!segment || !this.track) return;
      const metrics = this.getMetrics();
      if (!metrics || metrics.pages <= 1) return;
      const page = Number(segment.dataset.page || 0);
      const left = Math.min(metrics.maxScroll, page * metrics.viewport);
      this.track.scrollTo({ left, behavior: 'smooth' });
    }
  }

  customElements.define('hp-carousel-indicator', HpCarouselIndicator);
})();
